import React, { useCallback, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { connect } from 'react-redux';
import { useToggle } from 'components/shared/hooks/index';
import { Button, ButtonGroup } from 'ui';
import request from 'utils/request_ajax';
import Modal from 'components/shared/modal';
import LoadingModal from 'components/shared/LoadingModal';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';

const StatisticsByLearnerLink = ({ url }) => (
  <a
    target="_blank"
    rel="noopener noreferrer"
    className="c-brand-blue"
    href={url}
  >
    {t(
      'drafts.publishing.lesson_confirmation.tip.lesson_statistics_by_learner',
    )}
  </a>
);

const InvalidElementsModal = ({ hasUnpublishableElements, loading }) => (
  <Modal
    type="confirm"
    dismissCallback={null}
    loading
    show={hasUnpublishableElements && !loading}
  >
    <h2 className="mb-8">
      {t('drafts.publishing.lesson_confirmation.complete_required_fields')}
    </h2>
    <div>
      <Text variant={TEXT_VARIANTS.BODY}>
        {t('drafts.publishing.lesson_confirmation.another_user_has_edited')}
      </Text>
    </div>
    <ButtonGroup alignEnd>
      <Button
        onClick={() => {
          window.location.reload();
        }}
      >
        {t('common.refresh')}
      </Button>
    </ButtonGroup>
  </Modal>
);

const PublishButton = ({
  disabled,
  publishPath,
  redirectPath,
  lessonState,
  incompleteProgresses,
  lessonStatsPath,
  features,
}) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [confirmationVisible, toggleConfirmationVisible] = useToggle(false);
  const [hasUnpublishableElements, setHasUnpublishableElements] =
    useState(false);

  const closeModal = useCallback(() => {
    if (!loading) {
      toggleConfirmationVisible();
    }
  }, [loading, toggleConfirmationVisible]);

  const handlePublish = useCallback(() => {
    if (errorMessage) {
      setErrorMessage(null);
    }
    setLoading(true);
    request
      .post(publishPath)
      .then(() => {
        window.location = `${redirectPath}?publish_success=true`;
      })
      .fail(error => {
        setLoading(false);
        setErrorMessage(error.responseJSON.message);

        if (
          error.responseJSON.error &&
          error.responseJSON.error === 'has_incomplete_elements'
        ) {
          setHasUnpublishableElements(true);
        }
      });
  }, [errorMessage, publishPath, redirectPath]);

  const handlePublishButtonClick = useCallback(() => {
    if (confirmationVisible) {
      return;
    }

    if (!incompleteProgresses) {
      handlePublish();
    } else {
      toggleConfirmationVisible();
    }
  }, [
    confirmationVisible,
    toggleConfirmationVisible,
    handlePublish,
    incompleteProgresses,
  ]);

  return (
    <>
      <Modal
        show={confirmationVisible && !loading}
        dismissCallback={closeModal}
        title={t('drafts.publishing.lesson_confirmation.heading')}
      >
        {errorMessage && (
          <div className="ErrorContainer ErrorContainer--brandRed">
            {errorMessage}
          </div>
        )}
        <p>
          <b>{t('drafts.publishing.lesson_confirmation.body')}</b>
        </p>
        <p className="mt-12">
          <b>{t('drafts.publishing.lesson_confirmation.lower_body')}</b>
        </p>
        {features.lesson_analytics && (
          <p
            className="mb-16 mt-32"
            dangerouslySetInnerHTML={{
              __html: t('drafts.publishing.lesson_confirmation.tip.body', {
                link: renderToStaticMarkup(
                  <StatisticsByLearnerLink url={lessonStatsPath} />,
                ),
              }),
            }}
          />
        )}
        <ButtonGroup alignEnd>
          <Button disabled={loading} onClick={closeModal}>
            {t('common.cancel')}
          </Button>
          <Button disabled={disabled} onClick={handlePublish} primary>
            {t('drafts.publishing.lesson_confirmation.publish')}
          </Button>
        </ButtonGroup>
      </Modal>

      <InvalidElementsModal
        hasUnpublishableElements={hasUnpublishableElements}
        loading={loading}
      />

      {loading && (
        <LoadingModal
          tips={[
            t('drafts.publishing.tips.tip_1'),
            t('drafts.publishing.tips.tip_2'),
            t('drafts.publishing.tips.tip_3'),
            t('drafts.publishing.tips.tip_4'),
            t('drafts.publishing.tips.tip_5'),
          ]}
        />
      )}
      <div
        data-tooltip={t(
          'drafts.publishing.lesson_confirmation.complete_required_fields',
        )}
        className={disabled ? 'ButtonWithTooltip' : ''}
      >
        <Button disabled={disabled} onClick={handlePublishButtonClick} primary>
          {t('drafts.publishing.lesson_confirmation.publish')}
        </Button>
      </div>
    </>
  );
};

const mapStateToProps = ({ editor, meta }) => ({
  publishPath: editor.publish_path,
  redirectPath: editor.lesson_overview_path,
  lessonState: editor.lesson_state,
  incompleteProgresses: editor.incompleteProgresses,
  lessonStatsPath: editor.lesson_stats_path,
  features: meta.features,
});

export default connect(mapStateToProps)(PublishButton);
